import { useNavigate } from "react-router-dom"
import { useMemo, useState } from "react"
import { FieldType, Form, FormField, FormSeparator, ModalId, O, StaticValue, useUIActions, Validatable, ConfirmWarningsModal } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { Customer, HoardOrder, useHoardHooks } from "../../../../sales"
import { useActions } from "./actions"
import { AddressQuickFormModal, useAddressBuilders } from "../../../../../features/address"
import { Data2 } from "../../../../../features/data-types"
import { DeliveryData, DeliveryDataFields, deliveryHelpers } from "../../../../deliveries"
import { Item, ItemValidation, OperationItemFormModal, OperationItemsTable, useOperationBuilders, useOperationHooks, usePricesHooks } from "../../../../operations"
import { useManagerHooks } from "../../../../manager"
import { Lot, Product } from "../../../../products"
import { ExtendHoardValueModal } from "./extendHoardValueModal"
import { usePaymentTypeHooks } from "../../../../administration"

type Props = {
    table: TableName
    hoard: HoardOrder
    customer: Customer
    lots: Lot[]
}

export const EmitFinancialDeliveryOrderForm = (props: Props) => {
    const { table, hoard, lots } = props

    const defaultDeliveryData = useMemo(() => ({
        method: hoard.deliveryData?.method,
        addressId: hoard.deliveryData?.addressId,
        deliveryDate: deliveryHelpers.getDefaultDeliveryDate(hoard.deliveryData?.deliveryDate)
    }), [hoard.deliveryData])

    const [deliveryData, setDeliveryData] = useState<Partial<DeliveryData>>(defaultDeliveryData)
    const [itemsData, setItemsData] = useState<Partial<Item>[]>([])
    const [currentItem, setCurrentItem] = useState<Data2>()
    const [customer, setCustomer] = useState<Customer>(props.customer)
    const [newAddressId, setNewAddressId] = useState<string>()
    const [itemsValidations, setItemsValidations] = useState<string[]>([])
    const [stockWarnings, setStockWarnings] = useState<string[]>([])
    const [availableCredit, setAvailableCredit] = useState<O<number>>()

    const { currentBranchId } = useManagerHooks()
    const { getUsedCredit, getPendingCredit, getExtendedValue, getAvailableCredit } = useHoardHooks()
    const { isCreditAccount } = usePaymentTypeHooks()
    const isCreditAccountHoard = useMemo(() => isCreditAccount(hoard.paymentTypeId), [hoard.paymentTypeId])
    const { validateItems, getNegativeStockWarnings } = useOperationHooks()
    const pricesHooks = usePricesHooks()

    const { buildItem } = useOperationBuilders()
    const { buildAddress } = useAddressBuilders()
    const navigate = useNavigate()

    const { submitCustomerDeliveryAddress, emitDelivery, collectAndEmitDelivery } = useActions()
    const { openModal } = useUIActions()
    
    const itemMatches = (data: Data2, productId: string, lotId?: string) => data.productId === productId && data.lotId === lotId

    const selectedItems = useMemo(() => itemsData.map(itemData => {
        const hoardItem = hoard.items.find(item => itemMatches(itemData, item.productId, item.lotId))
        return buildItem({
            ...itemData,
            id: hoardItem?.id
        })
    }), [itemsData])
    const pendingCredit = useMemo(() => getPendingCredit(hoard, selectedItems), [hoard, selectedItems])
    const extendedValue = useMemo(() => getExtendedValue(hoard, selectedItems), [hoard, selectedItems])

    const defaultPrice = (productId: string) => {
        const price = hoard.pricesList[productId]
        return price || pricesHooks.product.adjustedPrice(productId, true, hoard.customerTypeId)
    }
    const filterProducts = (product: Product) => {
        const productLots = lots.filter(lot => lot.productId === product.id)
        const productItems = itemsData.filter(itemData => itemData.productId === product.id)
        if (currentItem?.productId === product.id) return true
        // The hoard keeps its prices list: what has no price there or today can't be delivered
        if (defaultPrice(product.id) <= 0) return false
        return productLots.length > productItems.length || (!product.loted && !itemsData.map(itemData => itemData.productId).includes(product.id))
    }
    const filterLots = (lot: Lot) => currentItem?.lotId === lot.id || !itemsData.map(itemData => itemData.lotId).includes(lot.id)

    const openFormModal = () => openModal(ModalId.OPERATION_ITEM_FORM)

    const onCreateItem = () => {
        setCurrentItem(undefined)
        setItemsValidations([])
        openFormModal()
    }

    const onEditItem = (productId: string, lotId?: string) => {
        setCurrentItem(itemsData.find(itemData => itemMatches(itemData, productId, lotId)))
        setItemsValidations([])
        openFormModal()
    }

    const onRemoveItem = (productId: string, lotId?: string) => {
        const newItemsData = itemsData.filter(itemData => !itemMatches(itemData, productId, lotId))
        setItemsData(newItemsData)
        setItemsValidations([])
    }

    const fields: FormField[] = [
        {
            name: 'delivery-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Datos de Entrega" />
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            render: () => <>{customer.name}</>
        },
        ...DeliveryDataFields({
            table,
            defaultData: deliveryData,
            customer,
            addressIdValue: newAddressId,
            branchId: hoard.branchId,
            isMethodOptional: false,
            isAddressOptional: false,
            isDeliveryDateOptional: false,
            showSeparator: false,
            onChange: setDeliveryData
        }),
        {
            name: 'items-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Selección de Productos" />
        },
        {
            name: 'hoardValue',
            type: FieldType.PRICE,
            label: 'Valor del Acopio',
            render: () => <StaticValue type={FieldType.PRICE} value={hoard.value!} />
        },
        {
            name: 'usedCredit',
            type: FieldType.PRICE,
            label: 'Crédito Utilizado',
            render: () => <StaticValue type={FieldType.PRICE} value={getUsedCredit(hoard.items)} />
        },
        {
            name: 'hoardItems',
            type: FieldType.TABLE,
            render: () => (
                <Validatable validations={itemsValidations}>
                    <OperationItemsTable
                        itemsData={itemsData}
                        createMode={false}
                        onCreateItem={onCreateItem}
                        onEditItem={onEditItem}
                        onRemoveItem={onRemoveItem}
                    />
                </Validatable>
            )
        },
        {
            name: 'pendingCredit',
            type: FieldType.PRICE,
            label: 'Crédito Pendiente',
            render: () => <StaticValue type={FieldType.PRICE} value={pendingCredit} />
        }
    ]

    const onSaveItem = (newData: Data2) => {
        const matches = (data_1: Data2, data_2: Data2) => itemMatches(data_1, data_2.productId! as string, data_2.lotId as O<string>)
        const newItemsData = currentItem ?
            itemsData.map(itemData => matches(itemData, currentItem) ? newData : itemData) :
            itemsData.find(itemData => matches(itemData, newData)) ? itemsData : [...itemsData, newData]
        setItemsData(newItemsData)
    }

    const validate = async () => {
        const validations = await validateItems(itemsData, [ItemValidation.NOT_EMPTY, ItemValidation.DEFINED_LOT, ItemValidation.AVAILABLE_STOCK], currentBranchId!)
        setItemsValidations(validations)
        return validations.length === 0
    }

    const submitDelivery = () => emitDelivery(
        table,
        hoard.id,
        deliveryData,
        itemsData
    )

    const collectAndSubmitDelivery = () => collectAndEmitDelivery(
        table,
        hoard.id,
        deliveryData,
        itemsData,
        extendedValue!.addedAmount
    )

    // Si la entrega supera el crédito pendiente, se confirma la ampliación del valor del acopio antes de emitir.
    // En cuenta corriente se consulta el crédito disponible para saber si el excedente se puede cargar a la cuenta
    const confirmExtensionAndSubmit = async (afterModal = false) => {
        if (extendedValue) {
            if (isCreditAccountHoard) {
                setAvailableCredit(await getAvailableCredit(hoard.customerId, true))
            }
            afterModal ? setTimeout(() => openModal(ModalId.EXTEND_HOARD_VALUE), 500) : openModal(ModalId.EXTEND_HOARD_VALUE)
        } else {
            await submitDelivery()
        }
    }

    const onSubmit = async () => {
        const valid = await validate()
        if (!valid) return
        // Productos habilitados para vender sin stock: se pide confirmar antes de emitir
        const warnings = await getNegativeStockWarnings(itemsData, currentBranchId!)
        if (warnings.length > 0) {
            setStockWarnings(warnings)
            openModal(ModalId.WARNING_CONFIRM)
        } else {
            await confirmExtensionAndSubmit()
        }
    }

    const onCancel = () => navigate(-1)

    // The new address is saved in the customer right away: otherwise the order points to an address that doesn't exist
    const onCreateCustomerAddress = async (data: Data2) => {
        const address = buildAddress(data)
        const savedCustomer = await submitCustomerDeliveryAddress(customer, address)
        setCustomer(savedCustomer)
        setNewAddressId(address.id)
        setDeliveryData({
            ...deliveryData,
            addressId: address.id
        })
    }

    return (
        <>
            <Form
                formId="emit-delivery-form"
                fields={fields}
                createLabel="Emitir Entrega"
                disableSubmit={itemsData.length === 0}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            <OperationItemFormModal
                defaultData={currentItem}
                withLots
                discountHint="Este descuento solo se aplicará a esta entrega"
                defaultPrice={defaultPrice}
                filterProducts={filterProducts}
                filterLots={filterLots}
                onSubmit={onSaveItem}
            />
            <AddressQuickFormModal onSubmit={onCreateCustomerAddress} />
            <ConfirmWarningsModal
                title="Sin stock disponible"
                warnings={stockWarnings}
                onConfirm={() => confirmExtensionAndSubmit(true)}
            />
            <ExtendHoardValueModal
                hoard={hoard}
                extendedValue={extendedValue?.value}
                pendingPayment={extendedValue?.pendingPayment}
                isCreditAccount={isCreditAccountHoard}
                addedAmount={extendedValue?.addedAmount}
                availableCredit={availableCredit}
                onSubmit={submitDelivery}
                onCollectNow={collectAndSubmitDelivery}
            />
        </>
    )
}
